import { t } from "i18next";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { MicDeviceType } from "@/common/enums/enums";
import type { TranscriptionProviderOption } from "@/common/types/types";
import { env } from "@/env";
import {
  NoiseReduction,
  TranscriptionLanguageWhisperAvailable,
  TranscriptionProviderName,
} from "@/hooks/transcriber/types";
import {
  type MicDevice,
  useAudioInputDevices,
} from "@/hooks/useAudioInputDevices";
import { useSelectTranscriptionProvider } from "@/hooks/visits/useSelectTranscriptionProvider";
import { useOwnDevices } from "@/lib/device-queries";
import { inputLanguageDisplayName } from "@/lib/note-language";
import {
  getRecordingMicrophoneId,
  setRecordingMicrophoneId,
} from "@/lib/recordingMicrophone";
import { useAppSettingsStore } from "@/store/appSettings.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";

export interface RecordingSetup {
  // Input language options ("auto" first when automatic detection works).
  inputLanguageOptions: { value: string; label: string }[];
  // What the input language control shows (never "auto" when unavailable).
  inputLanguageValue: string;
  setInputLanguage: (value: string) => void;
  // Microphones on this computer.
  mics: MicDevice[];
  loadingMics: boolean;
  // Linked phones (offered with live transcription only).
  mobileDevices: MicDevice[];
  loadingDevices: boolean;
  deviceError: boolean;
  refreshDevices: () => Promise<void>;
  micDevice: MicDevice | null;
  setMicDevice: (id: string) => void;
  noiseReduction: NoiseReduction;
  setNoiseReduction: (value: string) => void;
  noiseSuppression: boolean;
  setNoiseSuppression: (value: boolean) => void;
  isRealtimeEnabled: boolean;
  isProductionEnv: boolean;
  transcriptionProvider: {
    value: string;
    values: TranscriptionProviderOption[];
    handleChange: (value: string) => void;
  };
}

/**
 * The New visit settings (input language, microphone, noise options and the
 * transcription provider) with the store side effects they need: the default
 * microphone, a removed linked phone, "Detect automatically" only where it
 * works, and the live provider for the language. Used by the classic
 * Advanced settings and by the v3.2 New visit screen, so both behave alike.
 */
export const useRecordingSetup = (): RecordingSetup => {
  const {
    inputLanguage,
    noiseReduction,
    saveInputLanguage,
    saveNoiseReduction,
    isRealtimeEnabled,
    noiseSuppression,
    saveNoiseSuppression,
    saveMicDevice,
    micDevice,
    saveTranscriptionProvider,
  } = useRecordingSettingsStore();
  const { i18n } = useTranslation();
  const { mics, loading: loadingMics } = useAudioInputDevices();
  const { setInputLanguage: saveInputLanguageAppSettings } =
    useAppSettingsStore.getState();

  const {
    devices,
    ready,
    loading: loadingDevices,
    error: deviceError,
    refresh: refreshDevices,
  } = useOwnDevices();
  const mobileDevices: MicDevice[] = devices.map((device) => ({
    label: device.deviceName,
    value: device.id,
    default: false,
    type: MicDeviceType.MOBILE,
  }));
  const isProductionEnv = env.VITE_APP_ENVIRONMENT === "prod";
  useEffect(() => {
    if (
      micDevice?.type === MicDeviceType.MOBILE &&
      ready &&
      !devices.some((device) => device.id === micDevice.value)
    ) {
      saveMicDevice(mics.find((mic) => mic.default) ?? null);
    }
  }, [ready, devices, micDevice, mics, saveMicDevice]);

  // The device list refreshes on mount and whenever a device connects (a
  // Bluetooth headset reconnects when it is chosen), so keep the current
  // choice while it is still there, then the remembered one, then the default.
  useEffect(() => {
    if (!mics.length) return;
    const current = useRecordingSettingsStore.getState().micDevice;
    if (current?.type === MicDeviceType.MOBILE) return;
    const keep =
      mics.find((m) => m.value === current?.value) ??
      mics.find((m) => m.value === getRecordingMicrophoneId()) ??
      mics.find((m) => m.default);
    if (keep) saveMicDevice(keep);
  }, [mics, saveMicDevice]);

  // "Detect automatically" needs a transcriber that detects the language.
  // Live sessions do. A recording transcribed after Stop (Realtime off, or an
  // upload) is sent as "und" and only the worker with #332 detects it; an
  // older worker falls back to the note language. Gated until every
  // environment's worker has it.
  const autoLanguageAvailable =
    isRealtimeEnabled || AppConfig.enableNonRealtimeAutoLanguage;

  useEffect(() => {
    if (
      !autoLanguageAvailable &&
      (!inputLanguage || inputLanguage === "auto")
    ) {
      saveInputLanguage("eng");
      saveInputLanguageAppSettings("eng");
    }
  }, [
    autoLanguageAvailable,
    inputLanguage,
    saveInputLanguage,
    saveInputLanguageAppSettings,
  ]);

  useEffect(() => {
    if (!isRealtimeEnabled) {
      return;
    }
    const provider =
      inputLanguage === "eng"
        ? TranscriptionProviderName.DEEPGRAM
        : TranscriptionProviderName.OPENAI;
    saveTranscriptionProvider(provider);
  }, [isRealtimeEnabled, inputLanguage, saveTranscriptionProvider]);

  useEffect(() => {
    if (!isProductionEnv || !isRealtimeEnabled) {
      return;
    }
    if (inputLanguage === "eng") {
      saveTranscriptionProvider(TranscriptionProviderName.DEEPGRAM);
      return;
    }
    saveTranscriptionProvider(TranscriptionProviderName.OPENAI);
  }, [
    inputLanguage,
    isProductionEnv,
    isRealtimeEnabled,
    saveTranscriptionProvider,
  ]);

  const setNoiseReduction = (value: string): void => {
    const enumValue = value as NoiseReduction;
    if (
      enumValue === NoiseReduction.NEAR_FIELD ||
      enumValue === NoiseReduction.FAR_FIELD
    ) {
      saveNoiseReduction(enumValue);
    } else {
      saveNoiseReduction(NoiseReduction.FAR_FIELD);
    }
  };

  const setMicDevice = (id: string): void => {
    const devicesToSearch = [...mics, ...mobileDevices];
    const device = devicesToSearch.find((d) => d.value === id);
    if (device) {
      saveMicDevice(device);
      if (device.type === MicDeviceType.DESKTOP)
        setRecordingMicrophoneId(device.default ? null : device.value);
    }
  };

  const transcriptionProvider = useSelectTranscriptionProvider();
  const inputLanguageOptions = (
    autoLanguageAvailable
      ? TranscriptionLanguageWhisperAvailable
      : TranscriptionLanguageWhisperAvailable.filter(
          (language) => language.value !== "auto",
        )
  ).map((language) => ({
    value: language.value,
    label:
      language.value === "auto"
        ? t("noteLanguage.detectAutomatically")
        : inputLanguageDisplayName(
            language.value,
            i18n?.language,
            t("noteLanguage.mandarin"),
          ),
  }));
  const nonRealtimeInputLanguage =
    inputLanguage && inputLanguage !== "auto" ? inputLanguage : "eng";
  const inputLanguageValue = autoLanguageAvailable
    ? inputLanguage
    : nonRealtimeInputLanguage;

  return {
    inputLanguageOptions,
    inputLanguageValue,
    setInputLanguage: (value) => {
      saveInputLanguage(value);
      saveInputLanguageAppSettings(value);
    },
    mics,
    loadingMics,
    mobileDevices,
    loadingDevices,
    deviceError,
    refreshDevices,
    micDevice,
    setMicDevice,
    noiseReduction,
    setNoiseReduction,
    noiseSuppression,
    setNoiseSuppression: saveNoiseSuppression,
    isRealtimeEnabled,
    isProductionEnv,
    transcriptionProvider,
  };
};
